import React, { Component } from 'reactn'
import { Button, FormControl } from 'react-bootstrap'
import moment from 'moment'
import Api from '../Services/Api'
import SearchableSelect from './Components/SearchableSelect'
import SearchableMultiSelect from './Components/SearchableMultiSelect'
import DurationInput, { formatDurationDisplay, parseDurationInput } from './Components/DurationInput'
import { toast, confirmDialog, Modal, DataTable } from '../Components/ui'
import { FiSave, FiDownload, FiImage, FiFile, FiPlus, FiEdit2, FiTrash2, FiPaperclip } from 'react-icons/fi'
import './Styles/Maintenance.css'

const EMPTY_FORM = {
  _id: null,
  siteId: '',
  areaId: '',
  machineId: '',
  genericMachineName: '',
  type: 'ordinary',
  status: 'open',
  externalCompany: '',
  problemDescription: '',
  interventionDescription: '',
  sparePartsUsed: '',
  checksPerformed: true,
  startedAt: '',
  endedAt: '',
  workHours: '',
  downtimeHours: '',
  performedByName: '',
  maintainerIds: [],
  notes: '',
  files: []
}

const EMPTY_RECORD_FILTERS = {
  dateFrom: '',
  dateTo: '',
  siteId: '',
  areaId: '',
  machineId: '',
  status: '',
  type: '',
  maintainerId: ''
}

const EMPTY_HISTORY_FILTERS = {
  dateFrom: '',
  dateTo: '',
  siteId: '',
  areaId: '',
  machineId: ''
}

const REGISTRY_ENTITIES = [
  ['sites', 'Stabilimenti'],
  ['areas', 'Aree'],
  ['manufacturers', 'Fabbricanti'],
  ['frequencies', 'Frequenze'],
  ['machines', 'Macchine'],
  ['maintainers', 'Manutentori']
]

const TYPE_OPTIONS = [
  ['ordinary', 'Ordinaria'],
  ['extraordinary', 'Straordinaria'],
  ['scheduled', 'Programmata'],
  ['repair', 'Guasto / Riparazione'],
  ['construction', 'Costruzione / Modifica'],
  ['other', 'Altro']
]

const STATUS_OPTIONS = [
  ['draft', 'Bozza'],
  ['open', 'Aperta'],
  ['completed', 'Completata'],
  ['cancelled', 'Annullata']
]

class Maintenance extends Component {
  constructor (props) {
    super(props)
    this.state = {
      activeTab: 'dashboard',
      loading: false,
      sites: [],
      areas: [],
      manufacturers: [],
      maintainers: [],
      frequencies: [],
      machines: [],
      records: [],
      history: [],
      attachments: [],
      q: '',
      recordFilters: { ...EMPTY_RECORD_FILTERS },
      historyFilters: { ...EMPTY_HISTORY_FILTERS },
      historyFiltersOpen: false,
      recordFormOpen: false,
      form: { ...EMPTY_FORM },
      formAttachmentPreviews: {},
      importSummary: null,
      registryEntity: 'sites',
      registryFormOpen: false,
      registryForm: {}
    }
  }

  async componentDidMount () {
    await this.loadAll()
  }

  async loadAll () {
    this.setState({ loading: true })
    const [sites, areas, manufacturers, maintainers, frequencies, machines, records, history, attachments] = await Promise.all([
      Api.get('/maintenance/sites'),
      Api.get('/maintenance/areas'),
      Api.get('/maintenance/manufacturers'),
      Api.get('/maintenance/maintainers'),
      Api.get('/maintenance/frequencies'),
      Api.get('/maintenance/machines'),
      Api.get('/maintenance/records'),
      Api.get('/maintenance/history'),
      Api.get('/maintenance/attachments')
    ])

    this.setState({
      sites: sites.ok ? sites.data : [],
      areas: areas.ok ? areas.data : [],
      manufacturers: manufacturers.ok ? manufacturers.data : [],
      maintainers: maintainers.ok ? maintainers.data : [],
      frequencies: frequencies.ok ? frequencies.data : [],
      machines: machines.ok ? machines.data : [],
      records: records.ok ? records.data : [],
      history: history.ok ? history.data : [],
      attachments: attachments.ok ? attachments.data : [],
      loading: false
    })
  }

  mapById (items) {
    return (items || []).reduce((acc, item) => { acc[item._id] = item; return acc }, {})
  }

  machineLabel (machine) {
    if (!machine) return '-'
    return `${machine.code || '-'} — ${machine.description || '-'}`
  }

  machineSearchLabel (machine) {
    if (!machine) return '-'
    const site = this.state.sites.find(item => item._id === machine.siteId)
    const area = this.state.areas.find(item => item._id === machine.areaId)
    return [machine.code, machine.description, machine.serialNumber, site?.description, area?.description].filter(Boolean).join(' · ')
  }

  machineOptionLabel (machine) {
    if (!machine) return '-'
    const area = this.state.areas.find(item => item._id === machine.areaId)
    const areaLabel = area?.description ? ` · ${area.description}` : ''
    return `${machine.code || '-'} — ${machine.description || '-'}${areaLabel}`
  }

  sortByDescription (items) {
    return [...(items || [])].sort((left, right) => {
      const leftText = `${left.description || left.code || ''}`.toLowerCase()
      const rightText = `${right.description || right.code || ''}`.toLowerCase()
      return leftText.localeCompare(rightText, 'it')
    })
  }

  getArea (id) {
    return this.state.areas.find(area => area._id === id)
  }

  getSite (id) {
    return this.state.sites.find(site => site._id === id)
  }

  getAreasForSite (siteId, selectedAreaId) {
    const areas = this.state.areas.filter(area => !siteId || area.siteId === siteId)
    const selected = selectedAreaId ? this.getArea(selectedAreaId) : null
    if (selected && !areas.some(area => area._id === selected._id)) areas.push(selected)
    return this.sortByDescription(areas)
  }

  getMachinesForLocation (siteId, areaId, selectedMachineId) {
    const machines = this.state.machines.filter(machine => {
      if (siteId && machine.siteId !== siteId) return false
      if (areaId && machine.areaId !== areaId) return false
      return true
    })
    const selected = selectedMachineId ? this.getMachine(selectedMachineId) : null
    if (selected && !machines.some(machine => machine._id === selected._id)) machines.push(selected)

    return [...machines].sort((left, right) => {
      const leftText = `${left.code || ''} ${left.description || ''}`.toLowerCase()
      const rightText = `${right.code || ''} ${right.description || ''}`.toLowerCase()
      return leftText.localeCompare(rightText, 'it')
    })
  }

  getRecordLocation (record) {
    const machine = this.getMachine(record.machineId)
    return {
      siteId: (machine && machine.siteId) || record.siteId || '',
      areaId: (machine && machine.areaId) || record.areaId || '',
      machine
    }
  }

  getRecordMaintainerIds (record) {
    const location = this.getRecordLocation(record)
    const ids = [
      record.maintainerId,
      ...(Array.isArray(record.maintainerIds) ? record.maintainerIds : [])
    ].filter(Boolean).reduce((result, id) => {
      if (!result.includes(id)) result.push(id)
      return result
    }, [])

    if (ids.length === 0 && location.machine?.defaultMaintainerId) {
      ids.push(location.machine.defaultMaintainerId)
    }
    return ids
  }

  getRecordMaintainerNames (record) {
    const maintainerMap = this.mapById(this.state.maintainers)
    return this.getRecordMaintainerIds(record)
      .map(id => maintainerMap[id]?.description || id)
      .reduce((result, name) => {
        if (!result.includes(name)) result.push(name)
        return result
      }, [])
  }

  formatDurationField (value) {
    if (value === '' || value === null || value === undefined) return ''
    return formatDurationDisplay(value)
  }

  normalizeDurationField (value) {
    if (value === '' || value === null || value === undefined) return ''
    const parsed = parseDurationInput(value)
    return parsed === null ? value : formatDurationDisplay(parsed)
  }

  maybeAutoFillWorkHours () {
    const { form } = this.state
    if (!form.startedAt || !form.endedAt || form.workHours) return
    const start = moment(form.startedAt)
    const end = moment(form.endedAt)
    if (!start.isValid() || !end.isValid() || !end.isAfter(start)) return
    const hours = end.diff(start, 'minutes', true) / 60
    this.setState({ form: { ...form, workHours: formatDurationDisplay(Math.round(hours * 100) / 100) } })
  }

  getMachine (id) {
    return this.state.machines.find(machine => machine._id === id)
  }

  filtered (items, fields) {
    const q = this.state.q.trim().toLowerCase()
    if (!q) return items
    return items.filter(item => fields.some(field => {
      const value = field.split('.').reduce((obj, key) => obj && obj[key], item)
      return value && value.toString().toLowerCase().includes(q)
    }))
  }

  async runImport (mode) {
    if (mode === 'run') {
      confirmDialog({
        title: 'Import dati manutenzioni',
        text: 'Confermi import anagrafiche e storico legacy nei nuovi archivi manutenzione?',
        confirmLabel: 'Importa',
        onConfirm: async () => {
          const res = await Api.post('/maintenance/imports', { mode })
          if (res.ok) {
            this.setState({ importSummary: res.data })
            await this.loadAll()
            toast.success('Import completato')
          } else {
            toast.error('Import non riuscito')
          }
        }
      })
      return
    }

    const res = await Api.post('/maintenance/imports', { mode })
    if (res.ok) {
      this.setState({ importSummary: res.data })
      toast.success('Dry-run completato')
    } else {
      toast.error('Import non riuscito')
    }
  }

  revokeAttachmentPreviews () {
    Object.values(this.state.formAttachmentPreviews || {}).forEach(url => {
      if (url) window.URL.revokeObjectURL(url)
    })
  }

  closeRecordForm () {
    this.revokeAttachmentPreviews()
    this.setState({ recordFormOpen: false, form: { ...EMPTY_FORM }, formAttachmentPreviews: {} })
  }

  openNewRecord (machineId) {
    this.revokeAttachmentPreviews()
    const machine = machineId ? this.getMachine(machineId) : null
    this.setState({
      recordFormOpen: true,
      form: {
        ...EMPTY_FORM,
        performedByName: this.global?.user?.name || '',
        siteId: (machine && machine.siteId) || '',
        areaId: (machine && machine.areaId) || '',
        machineId: machineId || '',
        maintainerIds: machine?.defaultMaintainerId ? [machine.defaultMaintainerId] : [],
        startedAt: moment().format('YYYY-MM-DDTHH:mm')
      },
      formAttachmentPreviews: {}
    })
  }

  openEditRecord (record) {
    this.revokeAttachmentPreviews()
    const location = this.getRecordLocation(record)
    this.setState({
      recordFormOpen: true,
      form: {
        ...EMPTY_FORM,
        ...record,
        performedByName: record.performedByName || this.global?.user?.name || '',
        siteId: location.siteId,
        areaId: location.areaId,
        maintainerIds: this.getRecordMaintainerIds(record),
        startedAt: record.startedAt ? moment(record.startedAt).format('YYYY-MM-DDTHH:mm') : '',
        endedAt: record.endedAt ? moment(record.endedAt).format('YYYY-MM-DDTHH:mm') : '',
        workHours: this.formatDurationField(record.workHours),
        downtimeHours: this.formatDurationField(record.downtimeHours),
        files: []
      },
      formAttachmentPreviews: {}
    }, () => this.loadFormAttachmentPreviews(record._id))
  }

  isImageAttachment (attachment) {
    return attachment.kind === 'photo' || (attachment.mimeType || '').startsWith('image/')
  }

  getRecordAttachments (recordId) {
    return (this.state.attachments || []).filter(item => item.recordId === recordId)
  }

  async loadFormAttachmentPreviews (recordId) {
    const attachments = this.getRecordAttachments(recordId)
    const previews = {}
    await Promise.all(attachments.map(async attachment => {
      if (!this.isImageAttachment(attachment)) return
      const res = await Api.getBlob(`/maintenance/attachments/${attachment._id}/download`)
      if (res.ok) previews[attachment._id] = window.URL.createObjectURL(res.blob)
    }))
    if (this.state.recordFormOpen && this.state.form._id === recordId) {
      this.setState({ formAttachmentPreviews: previews })
    } else {
      Object.values(previews).forEach(url => window.URL.revokeObjectURL(url))
    }
  }

  updateForm (key, value) {
    this.setState({ form: { ...this.state.form, [key]: value } })
  }

  updateLocationForm (key, value) {
    const { form } = this.state
    const selectedMachine = this.getMachine(form.machineId)
    const currentMaintainerIds = form.maintainerIds || []
    const maintainerIds = selectedMachine?.defaultMaintainerId &&
      currentMaintainerIds.length === 1 &&
      currentMaintainerIds[0] === selectedMachine.defaultMaintainerId
      ? []
      : currentMaintainerIds

    if (key === 'siteId') {
      this.setState({
        form: {
          ...form,
          siteId: value,
          areaId: '',
          machineId: '',
          maintainerIds
        }
      })
      return
    }

    if (key === 'areaId') {
      this.setState({
        form: {
          ...form,
          areaId: value,
          machineId: '',
          maintainerIds
        }
      })
    }
  }

  updateMachineSelection (machineId) {
    const previousMachine = this.getMachine(this.state.form.machineId)
    const machine = this.getMachine(machineId)
    const currentMaintainerIds = this.state.form.maintainerIds || []
    const usingPreviousDefault = previousMachine?.defaultMaintainerId &&
      currentMaintainerIds.length === 1 &&
      currentMaintainerIds[0] === previousMachine.defaultMaintainerId
    const maintainerIds = (currentMaintainerIds.length === 0 || usingPreviousDefault)
      ? (machine?.defaultMaintainerId ? [machine.defaultMaintainerId] : [])
      : currentMaintainerIds

    this.setState({
      form: {
        ...this.state.form,
        machineId,
        siteId: machine ? (machine.siteId || this.state.form.siteId) : this.state.form.siteId,
        areaId: machine ? (machine.areaId || '') : this.state.form.areaId,
        genericMachineName: machineId ? '' : this.state.form.genericMachineName,
        maintainerIds
      }
    })
  }

  updateRecordFilter (key, value) {
    const filters = { ...this.state.recordFilters, [key]: value }
    if (key === 'siteId') {
      filters.areaId = ''
      filters.machineId = ''
    }
    if (key === 'areaId') {
      filters.machineId = ''
    }
    this.setState({ recordFilters: filters })
  }

  clearRecordFilters () {
    this.setState({ recordFilters: { ...EMPTY_RECORD_FILTERS } })
  }

  recordMatchesFilters (record) {
    const filters = this.state.recordFilters
    const location = this.getRecordLocation(record)

    if (filters.siteId && location.siteId !== filters.siteId) return false
    if (filters.areaId && location.areaId !== filters.areaId) return false
    if (filters.machineId && record.machineId !== filters.machineId) return false
    if (filters.status && record.status !== filters.status) return false
    if (filters.type && record.type !== filters.type) return false
    if (filters.maintainerId && !this.getRecordMaintainerIds(record).includes(filters.maintainerId)) return false

    const startedAt = record.startedAt ? moment(record.startedAt) : null
    if (filters.dateFrom && (!startedAt || startedAt.isBefore(moment(filters.dateFrom), 'day'))) return false
    if (filters.dateTo && (!startedAt || startedAt.isAfter(moment(filters.dateTo), 'day'))) return false

    return true
  }

  updateHistoryFilter (key, value) {
    const filters = { ...this.state.historyFilters, [key]: value }
    if (key === 'siteId') {
      filters.areaId = ''
      filters.machineId = ''
    }
    if (key === 'areaId') {
      filters.machineId = ''
    }
    this.setState({ historyFilters: filters })
  }

  clearHistoryFilters () {
    this.setState({ historyFilters: { ...EMPTY_HISTORY_FILTERS } })
  }

  historyMatchesFilters (record) {
    const filters = this.state.historyFilters
    if (filters.siteId && record.archivedSite?._id !== filters.siteId) return false
    if (filters.areaId && record.archivedArea?._id !== filters.areaId) return false
    if (filters.machineId && record.archivedMachine?._id !== filters.machineId) return false

    const date = record.date ? moment(record.date) : null
    if (filters.dateFrom && (!date || date.isBefore(moment(filters.dateFrom), 'day'))) return false
    if (filters.dateTo && (!date || date.isAfter(moment(filters.dateTo), 'day'))) return false

    return true
  }

  getHistorySites () {
    const seen = new Set()
    return this.state.history
      .filter(r => r.archivedSite?._id && !seen.has(r.archivedSite._id) && seen.add(r.archivedSite._id))
      .map(r => r.archivedSite)
  }

  getHistoryAreas (siteId) {
    const seen = new Set()
    return this.state.history
      .filter(r => r.archivedArea?._id && (!siteId || r.archivedSite?._id === siteId) && !seen.has(r.archivedArea._id) && seen.add(r.archivedArea._id))
      .map(r => r.archivedArea)
  }

  getHistoryMachines (siteId, areaId) {
    const seen = new Set()
    return this.state.history
      .filter(r => {
        if (!r.archivedMachine?._id) return false
        if (seen.has(r.archivedMachine._id)) return false
        if (siteId && r.archivedSite?._id !== siteId) return false
        if (areaId && r.archivedArea?._id !== areaId) return false
        seen.add(r.archivedMachine._id)
        return true
      })
      .map(r => r.archivedMachine)
  }

  async downloadHistoryReport () {
    const filters = {
      ...this.state.historyFilters,
      q: this.state.q.trim()
    }
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value)
    })

    const query = params.toString()
    const res = await Api.getBlob(`/maintenance/history/summary.pdf${query ? `?${query}` : ''}`)
    if (!res.ok) {
      toast.error('Impossibile scaricare il PDF dello storico')
      return
    }

    const url = window.URL.createObjectURL(res.blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `storico-manutenzioni-${moment().format('YYYY-MM-DD')}.pdf`
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  registryConfig (entity = this.state.registryEntity) {
    const configs = {
      sites: { label: 'Stabilimento', title: 'Stabilimenti', path: '/maintenance/sites', dataKey: 'sites' },
      areas: { label: 'Area', title: 'Aree', path: '/maintenance/areas', dataKey: 'areas' },
      manufacturers: { label: 'Fabbricante', title: 'Fabbricanti', path: '/maintenance/manufacturers', dataKey: 'manufacturers' },
      frequencies: { label: 'Frequenza', title: 'Frequenze', path: '/maintenance/frequencies', dataKey: 'frequencies' },
      machines: { label: 'Macchina', title: 'Macchine', path: '/maintenance/machines', dataKey: 'machines' },
      maintainers: { label: 'Manutentore', title: 'Manutentori', path: '/maintenance/maintainers', dataKey: 'maintainers' }
    }
    return configs[entity] || configs.sites
  }

  openRegistryForm (entity = this.state.registryEntity, item = null) {
    const config = this.registryConfig(entity)
    const base = {
      _id: item?._id || null,
      description: item?.description || '',
      legacyId: item?.legacyId || null
    }

    const registryForm = entity === 'areas'
      ? { ...base, siteId: item?.siteId || '' }
      : entity === 'machines'
        ? {
            _id: item?._id || null,
            legacyId: item?.legacyId || null,
            code: item?.code || '',
            description: item?.description || '',
            siteId: item?.siteId || '',
            areaId: item?.areaId || '',
            serialNumber: item?.serialNumber || '',
            model: item?.model || '',
            manufacturerId: item?.manufacturerId || '',
            defaultMaintainerId: item?.defaultMaintainerId || '',
            frequencyId: item?.frequencyId || '',
            documentationLocation: item?.documentationLocation || '',
            criticality: item?.criticality || '',
            prevention: item?.prevention || '',
            notes: item?.notes || '',
            status: item?.status || 'active'
          }
        : base

    this.setState({
      registryEntity: entity,
      registryFormOpen: true,
      registryForm,
      q: '',
      activeTab: 'registry',
      registryTitle: `${item ? 'Modifica' : 'Nuovo'} ${config.label.toLowerCase()}`
    })
  }

  updateRegistryForm (key, value) {
    const form = { ...this.state.registryForm, [key]: value }
    if (key === 'siteId') {
      form.areaId = ''
    }
    this.setState({ registryForm: form })
  }

  async saveRegistryItem () {
    const { registryEntity, registryForm } = this.state
    const config = this.registryConfig(registryEntity)

    if (!registryForm.description?.trim()) {
      toast.error('La descrizione è obbligatoria')
      return
    }
    if (registryEntity === 'areas' && !registryForm.siteId) {
      toast.error('Seleziona lo stabilimento dell\'area')
      return
    }
    if (registryEntity === 'machines' && !registryForm.siteId) {
      toast.error('Seleziona lo stabilimento della macchina')
      return
    }

    const payload = registryEntity === 'machines'
      ? {
          code: registryForm.code || null,
          description: registryForm.description,
          siteId: registryForm.siteId || null,
          areaId: registryForm.areaId || null,
          serialNumber: registryForm.serialNumber || null,
          model: registryForm.model || null,
          manufacturerId: registryForm.manufacturerId || null,
          defaultMaintainerId: registryForm.defaultMaintainerId || null,
          frequencyId: registryForm.frequencyId || null,
          documentationLocation: registryForm.documentationLocation || null,
          criticality: registryForm.criticality || null,
          prevention: registryForm.prevention || null,
          notes: registryForm.notes || null,
          status: registryForm.status || 'active'
        }
      : registryEntity === 'areas'
        ? {
            description: registryForm.description,
            siteId: registryForm.siteId
          }
        : {
            description: registryForm.description
          }

    const res = registryForm._id
      ? await Api.patch(`${config.path}/${registryForm._id}`, payload)
      : await Api.post(config.path, payload)

    if (!res.ok) {
      toast.error(res.data?.message || `Impossibile salvare ${config.label.toLowerCase()}`)
      return
    }

    await this.loadAll()
    this.setState({ registryFormOpen: false, registryForm: {} })
    toast.success(`${config.label} salvato`)
  }

  deleteRegistryItem (entity, item) {
    const config = this.registryConfig(entity)
    confirmDialog({
      title: `Elimina ${config.label.toLowerCase()}`,
      text: `Confermi l'eliminazione di "${item.description || item.code || item._id}"?`,
      confirmLabel: 'Elimina',
      variant: 'danger',
      onConfirm: async () => {
        const res = await Api.delete(`${config.path}/${item._id}`)
        if (!res.ok) {
          toast.error(res.data?.message || `Impossibile eliminare ${config.label.toLowerCase()}`)
          return
        }
        await this.loadAll()
        toast.success(`${config.label} eliminato`)
      }
    })
  }

  async saveRecord () {
    const { form } = this.state

    if (!form.machineId && !form.genericMachineName?.trim()) {
      toast.error('Seleziona un macchinario o inserisci il nome della macchina generica')
      return
    }
    if (!form.machineId && !form.siteId) {
      toast.error('Seleziona lo stabilimento della macchina generica')
      return
    }

    const workHours = parseDurationInput(form.workHours)
    const downtimeHours = parseDurationInput(form.downtimeHours)

    if (form.workHours && workHours === null) {
      toast.error('Formato ore lavoro non valido. Usa 2:30 oppure 2.5')
      return
    }

    if (form.downtimeHours && downtimeHours === null) {
      toast.error('Formato ore fermo non valido. Usa 1:15 oppure 1.25')
      return
    }

    const payload = {
      siteId: form.siteId || null,
      areaId: form.areaId || null,
      machineId: form.machineId || null,
      genericMachineName: form.genericMachineName || null,
      type: form.type,
      status: form.status,
      externalCompany: form.externalCompany,
      problemDescription: form.problemDescription,
      interventionDescription: form.interventionDescription,
      sparePartsUsed: form.sparePartsUsed,
      checksPerformed: form.checksPerformed,
      startedAt: form.startedAt ? new Date(form.startedAt).toISOString() : null,
      endedAt: form.endedAt ? new Date(form.endedAt).toISOString() : null,
      workHours,
      downtimeHours,
      performedByName: form.performedByName || this.global?.user?.name || '',
      maintainerIds: [...new Set((form.maintainerIds || []).filter(Boolean))],
      notes: form.notes
    }

    const res = form._id
      ? await Api.patch(`/maintenance/records/${form._id}`, payload)
      : await Api.post('/maintenance/records', payload)

    if (!res.ok) {
      toast.error(res.data?.message || 'Impossibile salvare la manutenzione')
      return
    }

    const record = res.data
    if (form.files && form.files.length > 0) {
      const upload = await Api.uploadMaintenanceAttachments(record._id, form.files)
      if (!upload.ok) toast.error('Manutenzione salvata, ma upload allegati non riuscito')
    }

    this.closeRecordForm()
    await this.loadAll()
    toast.success('Manutenzione salvata')
  }

  async downloadReport (record) {
    const res = await Api.getBlob(`/maintenance/records/${record._id}/report.pdf`)
    if (!res.ok) {
      toast.error('Impossibile scaricare il PDF')
      return
    }
    const url = window.URL.createObjectURL(res.blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${record.recordNumber || 'manutenzione'}.pdf`
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  async downloadSummaryReport () {
    const filters = {
      ...this.state.recordFilters,
      q: this.state.q.trim()
    }
    const params = new URLSearchParams()
    Object.entries(filters).forEach(([key, value]) => {
      if (value) params.set(key, value)
    })

    const query = params.toString()
    const res = await Api.getBlob(`/maintenance/records/summary.pdf${query ? `?${query}` : ''}`)
    if (!res.ok) {
      toast.error('Impossibile scaricare il PDF sintetico')
      return
    }
    const url = window.URL.createObjectURL(res.blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `manutenzioni-${moment().format('YYYY-MM-DD')}.pdf`
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  async downloadAttachment (attachment) {
    const res = await Api.getBlob(`/maintenance/attachments/${attachment._id}/download`)
    if (!res.ok) {
      toast.error('Impossibile scaricare allegato')
      return
    }
    const url = window.URL.createObjectURL(res.blob)
    const link = document.createElement('a')
    link.href = url
    link.download = attachment.originalName || 'allegato'
    document.body.appendChild(link)
    link.click()
    link.remove()
    window.URL.revokeObjectURL(url)
  }

  renderToolbar () {
    const { activeTab, q } = this.state
    const tabs = [
      ['dashboard', 'Dashboard'],
      ['machines', 'Macchine'],
      ['records', 'Manutenzioni'],
      ['history', 'Storico'],
      ['registry', 'Anagrafiche']
    ]

    return (
      <div className='maintenance-toolbar'>
        {tabs.map(tab => (
          <Button
            key={tab[0]}
            variant={activeTab === tab[0] ? 'primary' : 'outline-primary'}
            size='sm'
            onClick={() => this.setState({ activeTab: tab[0] })}
          >
            {tab[1]}
          </Button>
        ))}
        {activeTab !== 'dashboard' && (
          <FormControl
            value={q}
            placeholder='Cerca...'
            onChange={e => this.setState({ q: e.target.value })}
            style={{ maxWidth: 320, marginLeft: 10 }}
            size='sm'
          />
        )}
        <Button variant='primary' size='sm' style={{ marginLeft: 'auto' }} onClick={() => this.openNewRecord()}>
          <FiPlus style={{ marginRight: 4 }} /> Nuova manutenzione
        </Button>
      </div>
    )
  }

  renderDashboard () {
    const { machines, records, history } = this.state
    const openRecords = records.filter(r => r.status !== 'completed' && r.status !== 'cancelled')
    const completedThisMonth = records.filter(r => r.status === 'completed' && r.completedAt && moment(r.completedAt).isSame(moment(), 'month'))

    return (
      <div className='ui-stats-grid'>
        <div className='ui-stat-card'><strong>{machines.length}</strong><span>Macchine attive</span></div>
        <div className='ui-stat-card'><strong>{openRecords.length}</strong><span>Pratiche aperte</span></div>
        <div className='ui-stat-card'><strong>{completedThisMonth.length}</strong><span>Completate mese</span></div>
        <div className='ui-stat-card'><strong>{history.length}</strong><span>Record storico</span></div>
      </div>
    )
  }

  renderMachines () {
    const siteMap = this.mapById(this.state.sites)
    const areaMap = this.mapById(this.state.areas)
    const manufacturerMap = this.mapById(this.state.manufacturers)
    const maintainerMap = this.mapById(this.state.maintainers)
    const machines = this.filtered(this.state.machines, ['code', 'description', 'serialNumber', 'model'])

    const columns = [
      { key: 'code', header: 'Codice', accessor: 'code' },
      { key: 'description', header: 'Descrizione', accessor: 'description' },
      { key: 'site', header: 'Stabilimento', accessor: row => siteMap[row.siteId]?.description || '-' },
      { key: 'area', header: 'Area', accessor: row => areaMap[row.areaId]?.description || '-' },
      { key: 'serialNumber', header: 'Matricola', accessor: row => row.serialNumber || '-' },
      { key: 'model', header: 'Modello', accessor: row => row.model || '-' },
      { key: 'manufacturer', header: 'Fabbricante', accessor: row => manufacturerMap[row.manufacturerId]?.description || '-' },
      { key: 'maintainer', header: 'Manutentore', accessor: row => maintainerMap[row.defaultMaintainerId]?.description || '-' },
      {
        key: 'actions',
        header: '',
        accessor: '_id',
        sortable: false,
        render: (_, row) => (
          <>
            <Button size='sm' variant='outline-secondary' onClick={() => this.openRegistryForm('machines', row)} style={{ marginRight: 6 }}>
              <FiEdit2 size={14} />
            </Button>
            <Button size='sm' variant='primary' onClick={() => this.openNewRecord(row._id)}>Nuova</Button>
          </>
        )
      }
    ]

    return (
      <DataTable
        key={`machines-${this.state.q}`}
        data={machines}
        columns={columns}
        pagination
        pageSize={15}
        fillHeight
        emptyText='Nessuna macchina trovata.'
      />
    )
  }

  renderRecordFilters () {
    const { recordFilters, sites, maintainers } = this.state
    const areas = this.getAreasForSite(recordFilters.siteId, recordFilters.areaId)
    const machines = this.getMachinesForLocation(recordFilters.siteId, recordFilters.areaId, recordFilters.machineId)

    return (
      <div className='ui-form-section' style={{ marginBottom: 12 }}>
        <div className='ui-form-grid'>
          <div className='ui-field ui-col-2'>
            <label>Dal</label>
            <FormControl type='date' value={recordFilters.dateFrom} onChange={e => this.updateRecordFilter('dateFrom', e.target.value)} />
          </div>
          <div className='ui-field ui-col-2'>
            <label>Al</label>
            <FormControl type='date' value={recordFilters.dateTo} onChange={e => this.updateRecordFilter('dateTo', e.target.value)} />
          </div>
          <div className='ui-field ui-col-3'>
            <label>Stabilimento</label>
            <SearchableSelect options={this.sortByDescription(sites)} value={recordFilters.siteId} onChange={value => this.updateRecordFilter('siteId', value)} placeholder='Cerca stabilimento...' emptyLabel='Tutti gli stabilimenti' getOptionValue={item => item._id} getOptionLabel={item => item.description || '-'} />
          </div>
          <div className='ui-field ui-col-3'>
            <label>Area</label>
            <SearchableSelect options={areas} value={recordFilters.areaId} onChange={value => this.updateRecordFilter('areaId', value)} placeholder='Cerca area...' emptyLabel='Tutte le aree' getOptionValue={item => item._id} getOptionLabel={item => item.description || '-'} disabled={!recordFilters.siteId} />
          </div>
          <div className='ui-field ui-col-2'>
            <label>Stato</label>
            <FormControl as='select' value={recordFilters.status} onChange={e => this.updateRecordFilter('status', e.target.value)}>
              <option value=''>Tutti</option>
              {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </FormControl>
          </div>
          <div className='ui-field ui-col-4'>
            <label>Macchinario</label>
            <SearchableSelect
              options={machines}
              value={recordFilters.machineId}
              onChange={value => this.updateRecordFilter('machineId', value)}
              placeholder='Cerca macchina...'
              emptyLabel='Tutte le macchine'
              getOptionValue={machine => machine._id}
              getOptionLabel={machine => this.machineOptionLabel(machine)}
              getOptionSearchText={machine => this.machineSearchLabel(machine)}
              disabled={!recordFilters.siteId}
            />
          </div>
          <div className='ui-field ui-col-3'>
            <label>Tipo</label>
            <FormControl as='select' value={recordFilters.type} onChange={e => this.updateRecordFilter('type', e.target.value)}>
              <option value=''>Tutti</option>
              {TYPE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </FormControl>
          </div>
          <div className='ui-field ui-col-3'>
            <label>Manutentore</label>
            <SearchableSelect options={this.sortByDescription(maintainers)} value={recordFilters.maintainerId} onChange={value => this.updateRecordFilter('maintainerId', value)} placeholder='Cerca manutentore...' emptyLabel='Tutti i manutentori' getOptionValue={item => item._id} getOptionLabel={item => item.description || '-'} />
          </div>
          <div className='ui-field ui-col-2' style={{ justifyContent: 'flex-end' }}>
            <Button variant='outline-secondary' onClick={() => this.clearRecordFilters()}>Pulisci</Button>
          </div>
        </div>
      </div>
    )
  }

  renderRecords () {
    const attachmentsByRecord = this.state.attachments.reduce((acc, item) => {
      acc[item.recordId] = [...(acc[item.recordId] || []), item]
      return acc
    }, {})
    const records = this.filtered(this.state.records, ['recordNumber', 'genericMachineName', 'problemDescription', 'interventionDescription'])
      .filter(record => this.recordMatchesFilters(record))

    const statusBadge = (status) => {
      const map = { open: 'ui-badge-open', completed: 'ui-badge-completed', draft: 'ui-badge-draft', cancelled: 'ui-badge-cancelled' }
      const labelMap = { open: 'Aperta', completed: 'Completata', draft: 'Bozza', cancelled: 'Annullata' }
      return <span className={`ui-badge ${map[status] || ''}`}>{labelMap[status] || status}</span>
    }

    const typeLabel = type => TYPE_OPTIONS.find(option => option[0] === type)?.[1] || type || '-'

    const columns = [
      {
        key: 'startedAt',
        header: 'Inizio',
        accessor: 'startedAt',
        width: 126,
        style: { minWidth: 126, whiteSpace: 'nowrap' },
        render: value => value ? moment(value).format('DD/MM/YYYY HH:mm') : '-'
      },
      {
        key: 'endedAt',
        header: 'Fine',
        accessor: 'endedAt',
        width: 126,
        style: { minWidth: 126, whiteSpace: 'nowrap' },
        render: value => value ? moment(value).format('DD/MM/YYYY HH:mm') : '-'
      },
      { key: 'status', header: 'Stato', accessor: 'status', render: val => statusBadge(val) },
      { key: 'type', header: 'Tipo', accessor: 'type', render: value => typeLabel(value) },
      {
        key: 'location',
        header: 'Stabilimento / area',
        accessor: row => {
          const location = this.getRecordLocation(row)
          const site = this.getSite(location.siteId)?.description
          const area = this.getArea(location.areaId)?.description
          return [site, area].filter(Boolean).join('\n') || '-'
        },
        width: 180,
        style: { minWidth: 180 },
        render: (_, row) => {
          const location = this.getRecordLocation(row)
          const site = this.getSite(location.siteId)?.description
          const area = this.getArea(location.areaId)?.description
          const labels = [site, area].filter(Boolean)
          return (
            <div className='maintenance-location-cell'>
              {(labels.length ? labels : ['-']).map((label, index) => <span key={`${label}-${index}`}>{label}</span>)}
            </div>
          )
        }
      },
      {
        key: 'machine',
        header: 'Macchina',
        accessor: row => {
          const machine = this.getMachine(row.machineId)
          return machine ? this.machineLabel(machine) : row.genericMachineName
        },
        width: 210,
        style: { minWidth: 210 }
      },
      {
        key: 'maintainers',
        header: 'Tecnico / manutentori',
        accessor: row => [row.performedByName, ...this.getRecordMaintainerNames(row)].filter(Boolean).join(' · ') || '-',
        width: 220,
        style: { minWidth: 220 },
        render: (_, row) => {
          const names = this.getRecordMaintainerNames(row)
          return (
            <div className='maintenance-location-cell'>
              {row.performedByName && <span><strong>Tecnico:</strong> {row.performedByName}</span>}
              <span><strong>Manutentori:</strong> {names.join(', ') || '-'}</span>
            </div>
          )
        }
      },
      {
        key: 'workHours',
        header: 'Ore',
        accessor: row => row.workHours != null ? formatDurationDisplay(row.workHours) : '-',
        width: 90,
        style: { minWidth: 90, whiteSpace: 'nowrap' }
      },
      {
        key: 'summary',
        header: 'Problema / Intervento',
        accessor: row => [row.problemDescription, row.interventionDescription].filter(Boolean).join('\n') || '-',
        width: 280,
        style: { minWidth: 280 },
        render: (_, row) => (
          <div className='maintenance-summary-cell'>
            {row.problemDescription && <span><strong>Problema:</strong> {row.problemDescription}</span>}
            {row.interventionDescription && <span><strong>Intervento:</strong> {row.interventionDescription}</span>}
            {!row.problemDescription && !row.interventionDescription && <span>-</span>}
          </div>
        )
      },
      {
        key: 'attachments',
        header: 'Allegati',
        accessor: row => (attachmentsByRecord[row._id] || []).length,
        width: 92,
        style: { minWidth: 92, textAlign: 'center' },
        render: (count, row) => count
          ? (
            <button type='button' className='maintenance-attachment-link' onClick={() => this.openEditRecord(row)} title='Apri la manutenzione e visualizza gli allegati'>
              <FiPaperclip size={14} /> {count}
            </button>
            )
          : '-'
      },
      {
        key: 'actions',
        header: 'Azioni',
        accessor: '_id',
        sortable: false,
        width: 220,
        style: { minWidth: 220, whiteSpace: 'nowrap' },
        render: (_, row) => (
          <div className='maintenance-record-actions'>
            <Button size='sm' variant='outline-primary' onClick={() => this.openEditRecord(row)}>
              <FiEdit2 size={14} /> Apri
            </Button>
            <Button size='sm' variant='warning' onClick={() => this.downloadReport(row)}>
              <FiDownload size={14} /> Scarica PDF
            </Button>
          </div>
        )
      }
    ]

    return (
      <div className='maintenance-records-view' style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        {this.renderRecordFilters()}
        <div className='maintenance-toolbar' style={{ marginBottom: 8 }}>
          <Button variant='outline-primary' size='sm' onClick={() => this.downloadSummaryReport()}>
            <FiDownload style={{ marginRight: 4 }} /> PDF sintetico
          </Button>
          <span style={{ color: 'var(--sf-text-muted)', fontSize: 13 }}>{records.length} manutenzioni filtrate</span>
        </div>
        <DataTable
          key={`records-${this.state.q}-${JSON.stringify(this.state.recordFilters)}`}
          data={records}
          columns={columns}
          pagination
          pageSize={15}
          fillHeight
          emptyText='Nessuna manutenzione trovata.'
        />
      </div>
    )
  }

  renderHistoryFilters () {
    const { historyFilters, historyFiltersOpen } = this.state

    const hasActiveFilters = historyFilters.dateFrom || historyFilters.dateTo || historyFilters.siteId || historyFilters.areaId || historyFilters.machineId

    if (!historyFiltersOpen) {
      return (
        <div style={{ marginBottom: 8 }}>
          <Button variant={hasActiveFilters ? 'primary' : 'outline-secondary'} size='sm' onClick={() => this.setState({ historyFiltersOpen: true })}>
            ▸ Filtri{hasActiveFilters ? ' (attivi)' : ''}
          </Button>
        </div>
      )
    }

    const sites = this.sortByDescription(this.getHistorySites())
    const areas = this.sortByDescription(this.getHistoryAreas(historyFilters.siteId))
    const machines = this.getHistoryMachines(historyFilters.siteId, historyFilters.areaId)
      .sort((a, b) => `${a.code || ''} ${a.description || ''}`.localeCompare(`${b.code || ''} ${b.description || ''}`, 'it'))

    return (
      <div style={{ marginBottom: 12 }}>
        <Button variant={hasActiveFilters ? 'primary' : 'outline-secondary'} size='sm' onClick={() => this.setState({ historyFiltersOpen: false })} style={{ marginBottom: 8 }}>
          ▾ Filtri{hasActiveFilters ? ' (attivi)' : ''}
        </Button>
        <div className='ui-form-section'>
          <div className='ui-form-grid'>
            <div className='ui-field ui-col-2'>
              <label>Dal</label>
              <FormControl type='date' value={historyFilters.dateFrom} onChange={e => this.updateHistoryFilter('dateFrom', e.target.value)} />
            </div>
            <div className='ui-field ui-col-2'>
              <label>Al</label>
              <FormControl type='date' value={historyFilters.dateTo} onChange={e => this.updateHistoryFilter('dateTo', e.target.value)} />
            </div>
            <div className='ui-field ui-col-3'>
              <label>Stabilimento</label>
              <SearchableSelect options={sites} value={historyFilters.siteId} onChange={value => this.updateHistoryFilter('siteId', value)} placeholder='Cerca stabilimento...' emptyLabel='Tutti gli stabilimenti' getOptionValue={item => item._id} getOptionLabel={item => item.description || '-'} />
            </div>
            <div className='ui-field ui-col-3'>
              <label>Area</label>
              <SearchableSelect options={areas} value={historyFilters.areaId} onChange={value => this.updateHistoryFilter('areaId', value)} placeholder='Cerca area...' emptyLabel='Tutte le aree' getOptionValue={item => item._id} getOptionLabel={item => item.description || '-'} disabled={!historyFilters.siteId} />
            </div>
            <div className='ui-field ui-col-4'>
              <label>Macchinario</label>
              <SearchableSelect
                options={machines}
                value={historyFilters.machineId}
                onChange={value => this.updateHistoryFilter('machineId', value)}
                placeholder='Cerca macchina...'
                emptyLabel='Tutte le macchine'
                getOptionValue={machine => machine._id}
                getOptionLabel={machine => this.machineOptionLabel(machine)}
                getOptionSearchText={machine => `${machine.code || ''} ${machine.description || ''} ${machine.serialNumber || ''} ${machine.model || ''}`}
                disabled={!historyFilters.siteId}
              />
            </div>
            <div className='ui-field ui-col-2' style={{ justifyContent: 'flex-end' }}>
              <Button variant='outline-secondary' onClick={() => this.clearHistoryFilters()}>Pulisci</Button>
            </div>
          </div>
        </div>
      </div>
    )
  }

  renderHistory () {
    const sourceValue = (row, field, rawField = field) => {
      const value = row[field]
      if (value !== null && value !== undefined && value !== '') return value
      const rawValue = row.raw?.[rawField]
      return rawValue !== null && rawValue !== undefined && rawValue !== '' ? rawValue : '-'
    }
    const sourceDate = (row, field, rawField) => {
      const value = sourceValue(row, field, rawField)
      if (value === '-') return value
      const date = moment(value)
      return date.isValid() ? date.format('DD/MM/YYYY HH:mm') : value
    }
    const history = this.filtered(this.state.history, [
      'legacyId',
      'genericMachineName',
      'externalCompany',
      'problemDescription',
      'interventionDescription',
      'sparePartsUsed',
      'startTimeRaw',
      'endTimeRaw',
      'workHours',
      'downtimeHours',
      'legacyUserId',
      'archivedMachine.code',
      'archivedMachine.description',
      'archivedSite.description',
      'archivedArea.description',
      'historicalMachineName',
      'historicalSiteName',
      'historicalAreaName',
      'raw.id',
      'raw.macchinarioGenerico',
      'raw.ditta',
      'raw.descrizioneProblema',
      'raw.descrizioneIntervento',
      'raw.ricambioUtilizzato',
      'raw.dalle',
      'raw.alle',
      'raw.oreLavoro',
      'raw.oreFermo',
      'raw.idUtente'
    ]).filter(record => this.historyMatchesFilters(record))

    const columns = [
      {
        key: 'legacyId',
        header: 'ID storico',
        accessor: row => sourceValue(row, 'legacyId', 'id'),
        style: { minWidth: 90 }
      },
      {
        key: 'date',
        header: 'Data',
        accessor: row => sourceDate(row, 'date', 'dataOra'),
        style: { minWidth: 145, whiteSpace: 'nowrap' }
      },
      {
        key: 'site',
        header: 'Stabilimento',
        accessor: row => row.historicalSiteName || row.archivedSite?.description || 'Non disponibile',
        style: { minWidth: 150 }
      },
      {
        key: 'area',
        header: 'Area',
        accessor: row => row.historicalAreaName || row.archivedArea?.description || 'Non disponibile',
        style: { minWidth: 180 }
      },
      {
        key: 'machine',
        header: 'Macchina',
        accessor: row => row.historicalMachineName ||
          (row.archivedMachine ? this.machineLabel(row.archivedMachine) : null) ||
          row.genericMachineName ||
          'Macchinario generico non specificato',
        style: { minWidth: 260 }
      },
      {
        key: 'genericMachineName',
        header: 'Macchinario generico',
        accessor: row => sourceValue(row, 'genericMachineName', 'macchinarioGenerico'),
        style: { minWidth: 200 }
      },
      {
        key: 'externalCompany',
        header: 'Ditta',
        accessor: row => sourceValue(row, 'externalCompany', 'ditta'),
        style: { minWidth: 180 }
      },
      { key: 'problemDescription', header: 'Problema', accessor: row => sourceValue(row, 'problemDescription', 'descrizioneProblema'), style: { minWidth: 260 } },
      { key: 'interventionDescription', header: 'Intervento', accessor: row => sourceValue(row, 'interventionDescription', 'descrizioneIntervento'), style: { minWidth: 260 } },
      { key: 'sparePartsUsed', header: 'Ricambi', accessor: row => sourceValue(row, 'sparePartsUsed', 'ricambioUtilizzato'), style: { minWidth: 200 } },
      { key: 'startTimeRaw', header: 'Dalle', accessor: row => sourceValue(row, 'startTimeRaw', 'dalle'), style: { minWidth: 90, whiteSpace: 'nowrap' } },
      { key: 'endTimeRaw', header: 'Alle', accessor: row => sourceValue(row, 'endTimeRaw', 'alle'), style: { minWidth: 90, whiteSpace: 'nowrap' } },
      { key: 'workHours', header: 'Ore lavoro', accessor: row => sourceValue(row, 'workHours', 'oreLavoro'), style: { minWidth: 100 } },
      { key: 'downtimeHours', header: 'Ore fermo', accessor: row => sourceValue(row, 'downtimeHours', 'oreFermo'), style: { minWidth: 100 } },
      { key: 'legacyUserId', header: 'Utente legacy', accessor: row => sourceValue(row, 'legacyUserId', 'idUtente'), style: { minWidth: 105 } }
    ]

    return (
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <div className='ui-notice'>Dati storici importati dal sistema precedente. Non alimentano il nuovo ciclo manutenzioni.</div>
        {this.renderHistoryFilters()}
        <div className='maintenance-toolbar' style={{ marginBottom: 8 }}>
          <Button variant='outline-primary' size='sm' onClick={() => this.downloadHistoryReport()}>
            <FiDownload style={{ marginRight: 4 }} /> Scarica PDF
          </Button>
          <span style={{ color: 'var(--sf-text-muted)', fontSize: 13 }}>{history.length} record storico filtrati</span>
        </div>
        <DataTable
          key={`history-${this.state.q}-${JSON.stringify(this.state.historyFilters)}`}
          data={history}
          columns={columns}
          pagination
          pageSize={15}
          fillHeight
          emptyText='Nessun record storico trovato.'
        />
      </div>
    )
  }

  renderRegistry () {
    const { registryEntity, q } = this.state
    const config = this.registryConfig()
    const data = this.filtered(this.state[config.dataKey] || [], ['description', 'code', 'serialNumber', 'model'])
    const siteMap = this.mapById(this.state.sites)
    const areaMap = this.mapById(this.state.areas)
    const manufacturerMap = this.mapById(this.state.manufacturers)
    const maintainerMap = this.mapById(this.state.maintainers)
    const frequencyMap = this.mapById(this.state.frequencies)

    const actions = row => (
      <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
        <Button size='sm' variant='outline-secondary' onClick={() => this.openRegistryForm(registryEntity, row)}>
          <FiEdit2 size={14} />
        </Button>
        <Button size='sm' variant='outline-danger' onClick={() => this.deleteRegistryItem(registryEntity, row)}>
          <FiTrash2 size={14} />
        </Button>
      </div>
    )

    const baseColumns = [
      { key: 'description', header: 'Descrizione', accessor: 'description' },
      { key: 'legacyId', header: 'ID legacy', accessor: row => row.legacyId || '-' },
      { key: 'actions', header: '', accessor: '_id', sortable: false, render: (_, row) => actions(row), style: { width: 92 } }
    ]

    const columns = registryEntity === 'areas'
      ? [
          { key: 'description', header: 'Area', accessor: 'description' },
          { key: 'site', header: 'Stabilimento', accessor: row => siteMap[row.siteId]?.description || '-' },
          { key: 'legacyId', header: 'ID legacy', accessor: row => row.legacyId || '-' },
          { key: 'actions', header: '', accessor: '_id', sortable: false, render: (_, row) => actions(row), style: { width: 92 } }
        ]
      : registryEntity === 'machines'
        ? [
            { key: 'code', header: 'Codice', accessor: row => row.code || '-' },
            { key: 'description', header: 'Macchina', accessor: 'description' },
            { key: 'site', header: 'Stabilimento', accessor: row => siteMap[row.siteId]?.description || '-' },
            { key: 'area', header: 'Area', accessor: row => areaMap[row.areaId]?.description || '-' },
            { key: 'serialNumber', header: 'Matricola', accessor: row => row.serialNumber || '-' },
            { key: 'model', header: 'Modello', accessor: row => row.model || '-' },
            { key: 'manufacturer', header: 'Fabbricante', accessor: row => manufacturerMap[row.manufacturerId]?.description || '-' },
            { key: 'maintainer', header: 'Manutentore', accessor: row => maintainerMap[row.defaultMaintainerId]?.description || '-' },
            { key: 'frequency', header: 'Frequenza', accessor: row => frequencyMap[row.frequencyId]?.description || '-' },
            { key: 'actions', header: '', accessor: '_id', sortable: false, render: (_, row) => actions(row), style: { width: 92 } }
          ]
        : baseColumns

    return (
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
        <div className='maintenance-toolbar'>
          {REGISTRY_ENTITIES.map(([key, label]) => (
            <Button
              key={key}
              variant={registryEntity === key ? 'primary' : 'outline-primary'}
              size='sm'
              onClick={() => this.setState({ registryEntity: key, q: '' })}
            >
              {label}
            </Button>
          ))}
          <Button variant='primary' size='sm' style={{ marginLeft: 'auto' }} onClick={() => this.openRegistryForm(registryEntity)}>
            <FiPlus style={{ marginRight: 4 }} /> Nuovo
          </Button>
        </div>
        <DataTable
          key={`registry-${registryEntity}-${q}`}
          data={data}
          columns={columns}
          pagination
          pageSize={15}
          fillHeight
          emptyText={`Nessun elemento in ${config.title.toLowerCase()}.`}
        />
      </div>
    )
  }

  renderRegistryForm () {
    const { registryFormOpen, registryEntity, registryForm, registryTitle, sites, manufacturers, maintainers, frequencies } = this.state
    if (!registryFormOpen) return null

    const areaOptions = this.getAreasForSite(registryForm.siteId, registryForm.areaId)
    const config = this.registryConfig(registryEntity)

    return (
      <Modal
        isOpen
        onClose={() => this.setState({ registryFormOpen: false, registryForm: {} })}
        title={registryTitle || config.label}
        footer={
          <>
            <Button variant='secondary' onClick={() => this.setState({ registryFormOpen: false, registryForm: {} })}>Annulla</Button>
            <Button variant='primary' onClick={() => this.saveRegistryItem()}>
              <FiSave style={{ marginRight: 6 }} /> Salva
            </Button>
          </>
        }
      >
        <section className='ui-form-section'>
          <h6 className='ui-form-section-title'>{config.title}</h6>
          <div className='ui-form-grid'>
            {registryEntity === 'machines' && (
              <div className='ui-field ui-col-3'>
                <label>Codice</label>
                <FormControl value={registryForm.code || ''} onChange={e => this.updateRegistryForm('code', e.target.value)} />
              </div>
            )}
            <div className={`ui-field ${registryEntity === 'machines' ? 'ui-col-9' : 'ui-col-12'}`}>
              <label>Descrizione</label>
              <FormControl value={registryForm.description || ''} onChange={e => this.updateRegistryForm('description', e.target.value)} />
            </div>
            {(registryEntity === 'areas' || registryEntity === 'machines') && (
              <div className='ui-field ui-col-6'>
                <label>Stabilimento</label>
                <SearchableSelect options={this.sortByDescription(sites)} value={registryForm.siteId || ''} onChange={value => this.updateRegistryForm('siteId', value)} placeholder='Cerca stabilimento...' emptyLabel='Seleziona stabilimento' getOptionValue={item => item._id} getOptionLabel={item => item.description || '-'} />
              </div>
            )}
            {registryEntity === 'machines' && (
              <div className='ui-field ui-col-6'>
                <label>Area</label>
                <SearchableSelect options={areaOptions} value={registryForm.areaId || ''} onChange={value => this.updateRegistryForm('areaId', value)} placeholder='Cerca area...' emptyLabel='Nessuna area' getOptionValue={item => item._id} getOptionLabel={item => item.description || '-'} disabled={!registryForm.siteId} />
              </div>
            )}
            {registryEntity === 'machines' && (
              <>
                <div className='ui-field ui-col-3'>
                  <label>Matricola</label>
                  <FormControl value={registryForm.serialNumber || ''} onChange={e => this.updateRegistryForm('serialNumber', e.target.value)} />
                </div>
                <div className='ui-field ui-col-3'>
                  <label>Modello</label>
                  <FormControl value={registryForm.model || ''} onChange={e => this.updateRegistryForm('model', e.target.value)} />
                </div>
                <div className='ui-field ui-col-3'>
                  <label>Fabbricante</label>
                  <SearchableSelect options={this.sortByDescription(manufacturers)} value={registryForm.manufacturerId || ''} onChange={value => this.updateRegistryForm('manufacturerId', value)} placeholder='Cerca fabbricante...' emptyLabel='Nessun fabbricante' getOptionValue={item => item._id} getOptionLabel={item => item.description || '-'} />
                </div>
                <div className='ui-field ui-col-3'>
                  <label>Frequenza</label>
                  <SearchableSelect options={this.sortByDescription(frequencies)} value={registryForm.frequencyId || ''} onChange={value => this.updateRegistryForm('frequencyId', value)} placeholder='Cerca frequenza...' emptyLabel='Nessuna frequenza' getOptionValue={item => item._id} getOptionLabel={item => item.description || '-'} />
                </div>
                <div className='ui-field ui-col-4'>
                  <label>Manutentore</label>
                  <SearchableSelect options={this.sortByDescription(maintainers)} value={registryForm.defaultMaintainerId || ''} onChange={value => this.updateRegistryForm('defaultMaintainerId', value)} placeholder='Cerca manutentore...' emptyLabel='Nessun manutentore' getOptionValue={item => item._id} getOptionLabel={item => item.description || '-'} />
                </div>
                <div className='ui-field ui-col-4'>
                  <label>Stato</label>
                  <FormControl as='select' value={registryForm.status || 'active'} onChange={e => this.updateRegistryForm('status', e.target.value)}>
                    <option value='active'>Attiva</option>
                    <option value='inactive'>Non attiva</option>
                  </FormControl>
                </div>
                <div className='ui-field ui-col-4'>
                  <label>Documentazione</label>
                  <FormControl value={registryForm.documentationLocation || ''} onChange={e => this.updateRegistryForm('documentationLocation', e.target.value)} />
                </div>
                <div className='ui-field ui-col-6'>
                  <label>Criticità</label>
                  <FormControl as='textarea' className='compact' value={registryForm.criticality || ''} onChange={e => this.updateRegistryForm('criticality', e.target.value)} />
                </div>
                <div className='ui-field ui-col-6'>
                  <label>Prevenzione</label>
                  <FormControl as='textarea' className='compact' value={registryForm.prevention || ''} onChange={e => this.updateRegistryForm('prevention', e.target.value)} />
                </div>
                <div className='ui-field ui-col-12'>
                  <label>Note</label>
                  <FormControl as='textarea' className='compact' value={registryForm.notes || ''} onChange={e => this.updateRegistryForm('notes', e.target.value)} />
                </div>
              </>
            )}
          </div>
          {registryForm.legacyId ? <span className='ui-field-hint'>Record legacy ID {registryForm.legacyId}</span> : null}
        </section>
      </Modal>
    )
  }

  renderRecordMedia () {
    const { form, formAttachmentPreviews } = this.state
    const attachments = this.getRecordAttachments(form._id)
    if (!attachments.length) return null

    const images = attachments.filter(item => this.isImageAttachment(item))
    const documents = attachments.filter(item => !this.isImageAttachment(item))

    return (
      <section className='ui-form-section' style={{ marginTop: 8, paddingTop: 18, borderTop: '1px solid #e3e8e3' }}>
        <h6 className='ui-form-section-title'>
          <FiImage /> Media allegati ({attachments.length})
        </h6>
        {images.length > 0 && (
          <div className='maintenance-media-grid'>
            {images.map(attachment => (
              <button key={attachment._id} type='button' className='maintenance-media-card' onClick={() => this.downloadAttachment(attachment)} title={attachment.originalName}>
                {formAttachmentPreviews[attachment._id]
                  ? <img src={formAttachmentPreviews[attachment._id]} alt={attachment.originalName || 'Allegato'} className='maintenance-media-image' />
                  : <div className='maintenance-media-placeholder'><FiImage size={20} /><span>Caricamento...</span></div>}
                <span className='maintenance-media-caption'>{attachment.originalName}</span>
              </button>
            ))}
          </div>
        )}
        {documents.length > 0 && (
          <div className='maintenance-media-docs'>
            {documents.map(attachment => (
              <button key={attachment._id} type='button' className='maintenance-media-doc' onClick={() => this.downloadAttachment(attachment)}>
                <FiFile /> <span>{attachment.originalName}</span>
              </button>
            ))}
          </div>
        )}
      </section>
    )
  }

  renderRecordForm () {
    const { recordFormOpen, form, sites, maintainers } = this.state
    if (!recordFormOpen) return null

    const isGenericMachine = !form.machineId
    const areaOptions = this.getAreasForSite(form.siteId, form.areaId)
    const machineOptions = this.getMachinesForLocation(form.siteId, form.areaId, form.machineId)

    return (
      <Modal
        isOpen
        onClose={() => this.closeRecordForm()}
        title={form._id ? 'Modifica manutenzione' : 'Nuova manutenzione'}
        footer={
          <>
            <Button variant='secondary' onClick={() => this.closeRecordForm()}>Annulla</Button>
            <Button variant='primary' onClick={() => this.saveRecord()}>
              <FiSave style={{ marginRight: 6 }} /> Salva manutenzione
            </Button>
          </>
        }
      >
        <section className='ui-form-section'>
          <h6 className='ui-form-section-title'>Macchina e classificazione</h6>
          <div className='ui-form-grid'>
            <div className='ui-field ui-col-4'>
              <label>Stabilimento</label>
              <SearchableSelect
                options={this.sortByDescription(sites)}
                value={form.siteId || ''}
                onChange={value => this.updateLocationForm('siteId', value)}
                placeholder='Cerca stabilimento...'
                emptyLabel='Seleziona stabilimento'
                getOptionValue={item => item._id}
                getOptionLabel={item => item.description || '-'}
              />
            </div>
            <div className='ui-field ui-col-4'>
              <label>Area</label>
              <SearchableSelect
                options={areaOptions}
                value={form.areaId || ''}
                onChange={value => this.updateLocationForm('areaId', value)}
                placeholder='Cerca area...'
                emptyLabel='Tutte / nessuna area'
                getOptionValue={item => item._id}
                getOptionLabel={item => item.description || '-'}
                disabled={!form.siteId}
              />
            </div>
            <div className='ui-field ui-col-4'>
              <label>Macchinario</label>
              <SearchableSelect
                options={machineOptions}
                value={form.machineId || ''}
                onChange={value => this.updateMachineSelection(value)}
                placeholder='Cerca per codice, descrizione, matricola, area...'
                emptyLabel='Macchina generica (nessuna selezione)'
                getOptionValue={machine => machine._id}
                getOptionLabel={machine => this.machineOptionLabel(machine)}
                getOptionSearchText={machine => this.machineSearchLabel(machine)}
                disabled={!form.siteId}
              />
              <span className='ui-field-hint'>{form.siteId ? `${machineOptions.length} macchine nel filtro corrente` : 'Seleziona prima lo stabilimento'}</span>
            </div>
            <div className='ui-field ui-col-4'>
              <label>Tipo intervento</label>
              <FormControl as='select' value={form.type} onChange={e => this.updateForm('type', e.target.value)}>
                {TYPE_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </FormControl>
            </div>
            {isGenericMachine && (
              <div className='ui-field ui-col-8'>
                <label>Nome macchina generica</label>
                <FormControl value={form.genericMachineName || ''} placeholder='Es. Impianto FLW, Calibratrice 8 linee...' onChange={e => this.updateForm('genericMachineName', e.target.value)} />
              </div>
            )}
            <div className='ui-field ui-col-4'>
              <label>Stato pratica</label>
              <FormControl as='select' value={form.status} onChange={e => this.updateForm('status', e.target.value)}>
                {STATUS_OPTIONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
              </FormControl>
            </div>
          </div>
        </section>

        <section className='ui-form-section'>
          <h6 className='ui-form-section-title'>Tempi e durata</h6>
          <div className='ui-form-grid'>
            <div className='ui-field ui-col-3'>
              <label>Inizio intervento</label>
              <FormControl type='datetime-local' value={form.startedAt || ''} onChange={e => this.setState({ form: { ...form, startedAt: e.target.value } }, () => this.maybeAutoFillWorkHours())} />
            </div>
            <div className='ui-field ui-col-3'>
              <label>Fine intervento</label>
              <FormControl type='datetime-local' value={form.endedAt || ''} onChange={e => this.setState({ form: { ...form, endedAt: e.target.value } }, () => this.maybeAutoFillWorkHours())} />
            </div>
            <div className='ui-field ui-col-3'>
              <label>Ore lavoro</label>
              <DurationInput value={form.workHours || ''} onChange={value => this.updateForm('workHours', value)} onBlur={() => this.updateForm('workHours', this.normalizeDurationField(form.workHours))} />
              <span className='ui-field-hint'>Formato ore:minuti (2:30) o decimali (2.5)</span>
            </div>
            <div className='ui-field ui-col-3'>
              <label>Ore fermo macchina</label>
              <DurationInput value={form.downtimeHours || ''} onChange={value => this.updateForm('downtimeHours', value)} onBlur={() => this.updateForm('downtimeHours', this.normalizeDurationField(form.downtimeHours))} />
            </div>
          </div>
        </section>

        <section className='ui-form-section'>
          <h6 className='ui-form-section-title'>Responsabili</h6>
          <div className='ui-form-grid'>
            <div className='ui-field ui-col-4'>
              <label>Tecnico esecutore</label>
              <FormControl
                value={form.performedByName || ''}
                placeholder='Nome tecnico'
                onChange={() => {}}
                readOnly
                disabled
              />
            </div>
            <div className='ui-field ui-col-4'>
              <label>Manutentori partecipanti</label>
              <SearchableMultiSelect options={this.sortByDescription(maintainers)} value={form.maintainerIds || []} onChange={value => this.updateForm('maintainerIds', value)} placeholder='Cerca manutentore...' emptyLabel='Nessun manutentore' getOptionValue={item => item._id} getOptionLabel={item => item.description || '-'} />
            </div>
            <div className='ui-field ui-col-4'>
              <label>Ditta esterna</label>
              <FormControl value={form.externalCompany || ''} placeholder='Ragione sociale ditta esterna' onChange={e => this.updateForm('externalCompany', e.target.value)} />
            </div>
          </div>
        </section>

        <section className='ui-form-section'>
          <h6 className='ui-form-section-title'>Dettaglio intervento</h6>
          <div className='ui-form-grid'>
            <div className='ui-field ui-col-6'>
              <label>Descrizione problema</label>
              <FormControl as='textarea' value={form.problemDescription || ''} placeholder='Descrivi il guasto o la necessità di intervento...' onChange={e => this.updateForm('problemDescription', e.target.value)} />
            </div>
            <div className='ui-field ui-col-6'>
              <label>Descrizione intervento</label>
              <FormControl as='textarea' value={form.interventionDescription || ''} placeholder='Descrivi le operazioni eseguite...' onChange={e => this.updateForm('interventionDescription', e.target.value)} />
            </div>
            <div className='ui-field ui-col-6'>
              <label>Ricambi usati</label>
              <FormControl as='textarea' className='compact' value={form.sparePartsUsed || ''} placeholder='Elenco ricambi e codici' onChange={e => this.updateForm('sparePartsUsed', e.target.value)} />
            </div>
            <div className='ui-field ui-col-6'>
              <label>Note aggiuntive</label>
              <FormControl as='textarea' className='compact' value={form.notes || ''} placeholder='Note libere' onChange={e => this.updateForm('notes', e.target.value)} />
            </div>
          </div>
        </section>

        <section className='ui-form-section'>
          <h6 className='ui-form-section-title'>Allegati e verifiche</h6>
          <div className='ui-form-grid'>
            <div className='ui-field ui-col-6'>
              <div className='ui-checkbox-row'>
                <input type='checkbox' checked={!!form.checksPerformed} onChange={e => this.updateForm('checksPerformed', e.target.checked)} />
                <span>Verifiche effettuate</span>
              </div>
            </div>
            <div className='ui-field ui-col-6'>
              <div className='ui-upload-box'>
                <label>Allegati (foto, documenti)</label>
                <input type='file' multiple onChange={e => this.updateForm('files', e.target.files)} />
              </div>
            </div>
          </div>
        </section>

        {form._id ? this.renderRecordMedia() : null}
      </Modal>
    )
  }

  render () {
    const { activeTab, loading } = this.state

    return (
      <div className='page-fill'>
        <div className='ui-page-header'>
          <h3>Manutenzioni</h3>
        </div>
        {this.renderToolbar()}
        <div className='page-fill-body maintenance-body'>
          {loading && <p style={{ color: 'var(--sf-text-muted)' }}>Caricamento...</p>}
          {!loading && activeTab === 'dashboard' && this.renderDashboard()}
          {!loading && activeTab === 'machines' && this.renderMachines()}
          {!loading && activeTab === 'records' && this.renderRecords()}
          {!loading && activeTab === 'history' && this.renderHistory()}
          {!loading && activeTab === 'registry' && this.renderRegistry()}
        </div>
        {this.renderRecordForm()}
        {this.renderRegistryForm()}
      </div>
    )
  }
}

export default Maintenance
